import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { AuthService } from '@service/auth.service';
import { AccessType, UserAccess } from 'ops/common/models/access-settings';
import { lastValueFrom, take } from 'rxjs';
import * as _ from 'lodash';

@Component({
    selector: 'app-page-share-settings',
    templateUrl: './page-share-settings.component.html',
    styleUrls: ['./page-share-settings.component.scss'],
    standalone: false,
})
export class PageShareSettingsComponent implements OnInit {
    @Input() isPublic: boolean = true;
    @Input() usersAccess;
    @Input() inViewPage: boolean = false;
    @Input() isCreating: boolean = false;
    @Output() onAccessChanged: EventEmitter<boolean> = new EventEmitter();
    @Output() onUserPermissionChanged: EventEmitter<userPermission> = new EventEmitter();
    @Output() oncloseSettings: EventEmitter<MouseEvent> = new EventEmitter();
    @Output() onUpdateUsersAccess: EventEmitter<any> = new EventEmitter();
    @Output() onUpdatePageAccess: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('pageTypeInfo') pageTypeInfo: ElementRef;
    @ViewChild('accessTypeInfo') accessTypeInfo: ElementRef;

    @ViewChild('leavePageOverlay') leavePageOverlay: ConfirmationWindowComponent;

    roleTypeArray: { role: string; id: string }[] = [];
    usersPermissions: userPermission = {};

    showUsersOptions: boolean = false;
    showInfoPageType: boolean = false;
    showInfoAccessType: boolean = false;

    initialUsersPermissions: userPermission;
    initialPageAccessType: boolean;

    constructor(
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.pageTypeInfo?.nativeElement.contains(event.target) && this.showInfoPageType) {
            this.showInfoPageType = false;
        }

        if (!this.accessTypeInfo?.nativeElement.contains(event.target) && this.showInfoAccessType) {
            this.showInfoAccessType = false;
        }
    }

    async ngOnInit(): Promise<void> {
        this.roleTypeArray = await this.authService.getUserRoles();
        this.roleTypeArray.map((roleType) => {
            this.usersPermissions[roleType.id] = AccessType.NoAccess;
        });
        if (this.usersAccess?.length > 0) {
            this.usersAccess.map((userAccess) => {
                this.usersPermissions[userAccess.roleId] = userAccess.accessLevel;
            });
        }

        this.initialUsersPermissions = _.cloneDeep(this.usersPermissions);
        this.initialPageAccessType = this.isPublic;

        this.onUserPermissionChanged.emit(this.usersPermissions);
        this.cdr.markForCheck();
    }

    updateShareSetting() {
        if (this.disableLockOptions() && !this.isCreating) {
            return;
        }
        this.isPublic = this.isPublic ? false : true;
        this.onAccessChanged.emit(this.isPublic);
    }

    handleUserAccessChanged(newUserAccess: UserAccess) {
        this.usersPermissions[newUserAccess.roleId] = newUserAccess.accessLevel;
        this.onUserPermissionChanged.emit(this.usersPermissions);
    }

    async savePermissions(e: MouseEvent) {
        if (!_.isEqual(this.initialUsersPermissions, this.usersPermissions)) {
            const updatedUsersAccess = [];

            let roleIdsInUsersAcces = new Set(
                this.usersAccess.map((userAcces) => userAcces.roleId),
            );

            this.usersAccess.forEach((userAcces) => {
                if (
                    this.initialUsersPermissions[userAcces.roleId] !==
                    this.usersPermissions[userAcces.roleId]
                ) {
                    const updatedAccess = userAcces;
                    updatedAccess.accessLevel = this.usersPermissions[userAcces.roleId];
                    updatedUsersAccess.push(updatedAccess);
                }
            });

            Object.keys(this.usersPermissions).forEach((id) => {
                if (!roleIdsInUsersAcces.has(id)) {
                    const newUserAccess = {
                        roleId: id,
                        accessLevel: this.usersPermissions[id],
                    };
                    updatedUsersAccess.push(newUserAccess);
                }
            });
            this.onUpdateUsersAccess.emit(updatedUsersAccess);
        }
        if (!_.isEqual(this.initialPageAccessType, this.isPublic)) {
            this.onUpdatePageAccess.emit(this.isPublic);
        }

        this.oncloseSettings.emit(e);
    }

    async handleCancel(e: MouseEvent) {
        if (!this.disableSaveButton()) {
            this.leavePageOverlay.setVisible(true);
            const leavePage = await lastValueFrom(
                this.leavePageOverlay.onOptionSelected.pipe(take(1)),
            );

            if (leavePage) {
                this.oncloseSettings.emit(e);
            }
            return;
        } else {
            this.oncloseSettings.emit(e);
        }
    }

    disableSaveButton(): boolean {
        if (!_.isEqual(this.initialPageAccessType, this.isPublic)) {
            return false;
        }
        return _.isEqual(this.initialUsersPermissions, this.usersPermissions);
    }

    disableLockOptions() {
        const currentUserId = this.authService.getCurrentUserId();
        return this.usersPermissions[currentUserId] !== AccessType.Owner;
    }

    openPageTypeInfo(event) {
        this.showInfoAccessType = false;
        this.showInfoPageType = !this.showInfoPageType;
        event.stopPropagation();
    }

    openAccessTypeInfo(event) {
        this.showInfoPageType = false;
        this.showInfoAccessType = !this.showInfoAccessType;
        event.stopPropagation();
    }
}

export interface userPermission {
    [key: string]: AccessType;
}
